import { isDark } from "@chakra-ui/theme-tools";
import Color from "color";

/**
 * generateIntegratorColorScale
 * @param {string} mainColor - The primary color an integrator provides us with in their theme config
 * @returns {Object.<number, string>} Color values based on a primary color
 * @description
 * This function provides us with a color scale for an integrator, derived from a single color. This
 * makes the integration from our theme config to the way Chakra handles hover, active and focus
 * states, completely seamless.
 */
export function generateIntegratorColorScale(mainColor) {
  const lightnessDelta = 9;
  const darknessDelta = 10;

  const scale = {
    50: Color(mainColor)
      .lightness(50 + lightnessDelta * 5)
      .hex(),
    100: Color(mainColor)
      .lightness(50 + lightnessDelta * 4)
      .hex(),
    200: Color(mainColor)
      .lightness(50 + lightnessDelta * 3)
      .hex(),
    300: Color(mainColor)
      .lightness(50 + lightnessDelta * 2)
      .hex(),
    400: Color(mainColor)
      .lightness(50 + lightnessDelta * 1)
      .hex(),
    500: Color(mainColor).hex(),
    600: Color(mainColor)
      .lightness(50 - darknessDelta * 1)
      .hex(),
    700: Color(mainColor)
      .lightness(50 - darknessDelta * 2)
      .hex(),
    800: Color(mainColor)
      .lightness(50 - darknessDelta * 3)
      .hex(),
    900: Color(mainColor)
      .lightness(50 - darknessDelta * 4)
      .hex(),
  };

  return scale;
}

/**
 * solidButtonShades
 * @param {string} rest - Concrete resting background color of the button (e.g. `integrator.500`)
 * @param {Object} theme - Chakra theme, used to resolve the `isDark` check
 * @returns {{ color: string, hover: string, active: string }} Text color and hover/active backgrounds
 * @description
 * Derives hover/active backgrounds by mixing the resting shade 16%/32% toward black (white text)
 * or white (black text), so each state increases text contrast. Near the lightness extremes that
 * direction has no headroom, so it flips to keep a visible state change for any brand primary.
 *
 * Don't use `.600`/`.700` for hover/active: generateIntegratorColorScale pins those to absolute
 * HSL lightness, so for some primaries they equal or are lighter than `.500`.
 */
export function solidButtonShades(rest, theme) {
  // Text color is chosen once from the resting bg so it can't flip between states.
  const color = isDark(rest)(theme) ? "white" : "black";

  const restColor = Color(rest);
  const towardWhite = color === "black";
  const noHeadroom = towardWhite
    ? restColor.lightness() > 84
    : restColor.lightness() < 16;
  const shiftToward = Color(noHeadroom !== towardWhite ? "white" : "black");

  return {
    color,
    hover: restColor.mix(shiftToward, 0.16).hex(),
    active: restColor.mix(shiftToward, 0.32).hex(),
  };
}
